import { Alert, Button, Drawer, Empty, Space, Table, Tag, Typography } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";
import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
import type { useSalesPerformanceRuntime } from "../runtime";
import type { PerformanceDetailRow } from "../types";

/** 一行对应一次绩效，展开组内全部依据并保留可点击的附件原地址。 */
export function SalesPerformanceDetails({ runtime, canExport }: {
  runtime: ReturnType<typeof useSalesPerformanceRuntime>; canExport: boolean;
}) {
  const selection = runtime.detailSelection;
  const query = runtime.detailsQuery;
  const data = query.isError || query.isFetching ? undefined : query.data;
  const columns: ColumnProps<PerformanceDetailRow>[] = [
    { title: "联系人", width: 230, render: (_, row) => <Space direction="vertical" size={4}>
      <Typography.Text bold>{row.contact_name || `联系人 ${row.contact_id}`}</Typography.Text>
      <span>{row.contact_full_name || "—"}</span>
      <Typography.Text type="secondary">{row.mobile || "无手机号"}</Typography.Text>
    </Space> },
    { title: "计次依据", width: 180, render: (_, row) => <Space direction="vertical" size={6}>
      <Tag color="purple">计 1 次</Tag><span>{row.group_label || "有效动态"}</span>
      <Typography.Text type="secondary">{row.evidence.length} 条原始记录</Typography.Text>
    </Space> },
    { title: "记录详情与附件", render: (_, row) => <div className="sales-performance-evidence-list">
      {row.evidence.map((item, index) => <details key={`${item.source_id}:${index}`}
        className="sales-performance-evidence" open={row.evidence.length === 1}>
        <summary>{item.occurred_at.replace("T", " ")} · {item.category || "动态记录"} · ID {item.source_id}</summary>
        <div className="sales-performance-evidence-body">
          {item.trip_start && <div>团期：{item.trip_start} 至 {item.trip_end}</div>}
          {item.meeting_name && <div>会议名称：{item.meeting_name}</div>}
          <div className="sales-performance-detail-content">{item.content || "无文字内容"}</div>
          {item.meeting_desc && <div className="sales-performance-detail-content">
            {selection?.metric === "gifts" ? "收礼备注" : "会议详情"}：{item.meeting_desc}
          </div>}
          {item.labels && <div>标签：{item.labels.replaceAll("▲", "、")}</div>}
          {!!item.gift_items?.length && <div>礼品明细：{item.gift_items.join("；")}</div>}
          {item.attachments?.map((attachment) => <div key={attachment.id} className="sales-performance-attachment">
            {attachment.content && <div>{attachment.content}</div>}
            {attachment.link_url ? <a href={resolveHttpResourceUrl(attachment.link_url)} target="_blank" rel="noopener noreferrer">
              {attachment.file_url}
            </a> : <span>{attachment.file_url}（无法直接打开）</span>}
          </div>)}
        </div>
      </details>)}
    </div> },
  ];
  return <Drawer visible={Boolean(selection)} width="min(1380px, 95vw)" footer={null}
    onCancel={runtime.closeDetails} title={selection ? `${selection.month} · ${selection.user_name} · ${selection.metric_label}` : "绩效明细"}>
    <div className="sales-performance-details">
      <Space className="sales-performance-detail-toolbar">
        <Typography.Text>明细合计：{data?.total ?? "—"} 次</Typography.Text>
        {canExport && <Button type="primary" onClick={runtime.exportDetails}
          loading={runtime.isDetailsExporting} disabled={!data}>导出全部明细</Button>}
      </Space>
      <Alert type="info" content={selection?.metric === "profile_fields"
        ? "每个画像字段计 1 次，时间及记录人取首次采集记录；内容展示联系人当前值。"
        : "每行计 1 次；同组多条原始记录展开查看，不重复计次。导出包含该人员、月份及指标的全部明细。"} />
      {query.isError && <Alert type="error" content="明细读取失败，请重试。"
        action={<Button size="small" onClick={() => void query.refetch()}>重试</Button>} />}
      {runtime.detailsExportError && <Alert type="error" content="明细导出失败，请稍后重试。" />}
      <Table<PerformanceDetailRow> rowKey="key" columns={columns} data={data?.rows ?? []}
        loading={query.isFetching} border={{ cell: true }} scroll={{ x: 1000 }}
        noDataElement={<Empty description={query.isError ? "请重试查询" : "当月该指标暂无明细"} />}
        pagination={{ current: runtime.detailsPage, pageSize: runtime.detailsPageSize,
          total: data?.total ?? 0, showTotal: true, sizeCanChange: true, sizeOptions: [20, 50, 100],
          onChange: runtime.setDetailsPage, onPageSizeChange: runtime.changeDetailsPageSize }} />
    </div>
  </Drawer>;
}
